// SingleGenomeBrowser.jsx - Fixed DOM manipulation and cleanup
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Alert } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import Files from "../../Components/Common/Files";
import SelectedFilesDisplay from "../../Components/Common/SelectedFilesDisplay";
import BreadCrumb from "../../Components/Common/BreadCrumb";

// API
import { getSingleGenomeBrowserData } from "../../helpers/eirna_backend_helper";

// Hooks
import { useFileSelection } from "../../hooks/useFileSelection";
import { GenomeBrowserTranscriptListener } from "../../hooks/useGenomeBrowserTranscriptListener";

const SingleGenomeBrowser = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const scriptElementRef = useRef(null);

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [plotScript, setPlotScript] = useState(null);
  const [bokehLoading, setBokehLoading] = useState(false);
  const [bokehElementId, setBokehElementId] = useState(null);

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    message: null,
    script: null,
  });

  // File selection hook
  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    setSelectedSeqType,
    setSelectedGroup,
    selectMultipleFiles,
    deselectMultipleFiles,
  } = useFileSelection("riboseq");

  // Cleanup function
  const cleanup = () => {
    // Clean up script element
    if (
      scriptElementRef.current &&
      document.head.contains(scriptElementRef.current)
    ) {
      try {
        document.head.removeChild(scriptElementRef.current);
        scriptElementRef.current = null;
      } catch (e) {
        console.warn("Script element cleanup failed:", e);
      }
    }
  };

  // Execute Bokeh script
  useEffect(() => {
    if (plotScript && window.Bokeh && containerRef.current) {
      setBokehLoading(true);

      // Cleanup previous script
      cleanup();

      try {
        // Extract script ID from the original script tag
        const scriptIdMatch = plotScript.match(/id="([^"]+)"/);
        const scriptId = scriptIdMatch
          ? scriptIdMatch[1]
          : `bokeh-${Date.now()}`;

        console.log("Creating Bokeh element with ID:", scriptId);
        setBokehElementId(scriptId);

        // Clear container and create the target element FIRST
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          const targetDiv = document.createElement("div");
          targetDiv.id = scriptId;
          targetDiv.style.width = "100%";
          targetDiv.style.minHeight = "600px";
          containerRef.current.appendChild(targetDiv);

          // Now extract and execute the script content
          const scriptContent = plotScript.replace(
            /<script[^>]*>|<\/script>/g,
            ""
          );

          // Execute the script in a way that preserves the global context
          const scriptElement = document.createElement("script");
          scriptElement.textContent = scriptContent;
          scriptElementRef.current = scriptElement;

          // Add the script to head to execute it
          document.head.appendChild(scriptElement);

          // Check if plot loaded after a delay
          setTimeout(() => {
            setBokehLoading(false);
            const element = document.getElementById(scriptId);
            if (element && element.children.length > 0) {
              console.log("Bokeh plot loaded successfully");
            } else {
              console.warn("Bokeh plot may not have loaded properly");
            }
          }, 3000);
        }
      } catch (err) {
        console.error("Error executing Bokeh script:", err);
        setBokehLoading(false);
        setError("Failed to render genome browser plot");
      }
    }

    // Cleanup function for useEffect
    return cleanup;
  }, [plotScript]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, []);

  // Exposes 'selectedFiles' to bokeh which allows the update button js to work
  useEffect(() => {
    window.bokehBridge = window.bokehBridge || {};
    window.bokehBridge.selectedFiles = selectedFiles;
  }, [selectedFiles]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const html_params = new URLSearchParams(location.search);
        const response = await getSingleGenomeBrowserData(projectId, html_params);
        console.log("API Response:", response);
        setApiData(response);

        // Handle message
        if (response.message && typeof response.message === "string") {
          setMessage(response.message.trim());
        }

        // Set script for plot rendering
        if (response.script) {
          console.log("Plot Script >>>", response.script);
          setPlotScript(response.script);
        }

        // Set default seq type
        const fileTypes = [
          ...new Set(response.json_files?.map((file) => file.file_type) || []),
        ];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }
      } catch (err) {
        console.error("API Error:", err);
        setError("Failed to load genome browser data");
        toast.error("Failed to load genome browser data");
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId, setSelectedSeqType, setSelectedGroup]);

  // Filter files and other logic
  const filteredFiles = React.useMemo(() => {
    let files = apiData.json_files || [];
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((file) => file.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((file) => file.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  const seqTypes = React.useMemo(() => {
    const types = [
      ...new Set(apiData.json_files?.map((file) => file.file_type) || []),
    ];
    return types.map((type) => ({
      value: type,
      label: type,
      count:
        apiData.json_files?.filter((file) => file.file_type === type).length ||
        0,
    }));
  }, [apiData.json_files]);

  const groups = React.useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );
    return Array.from(groupSet)
      .filter((group) => group)
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group)
            .length || 0,
      }));
  }, [apiData.json_files]);

  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;
      const groupFiles = filteredFiles.filter(
        (x) => x.subgroup === selectedGroup
      );
      const fileIds = groupFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Single Genome Browser | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Single Genome Browser" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <h4
                style={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#1f2937",
                  marginBottom: "0",
                }}
              >
                Single Genome Browser
              </h4>
            </Col>
          </Row>

          {/* Message Alert */}
          {message && (
            <Row className="mb-4">
              <Col lg={12}>
                <Alert color="info">
                  <i className="ri-information-line me-2"></i>
                  {message}
                </Alert>
              </Col>
            </Row>
          )}

          {/* Error Alert */}
          {error && (
            <Row className="mb-4">
              <Col lg={12}>
                <Alert color="danger">
                  <i className="ri-error-warning-line me-2"></i>
                  {error}
                </Alert>
              </Col>
            </Row>
          )}

          {/* File Selection Components */}
          <Row className="mb-4">
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={groups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                showSelectAllCheckboxes={false}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col lg={12}>
              <Files
                files={filteredFiles}
                selectedFiles={selectedFiles}
                onToggleFile={toggleFile}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                title="Available Files"
                searchable={true}
                selectable={true}
                filesPerPage={20}
                columns={6}
                showFileCount={true}
              />

              {/* Selected Files Display */}
              <SelectedFilesDisplay
                selectedFiles={selectedFiles}
                allFiles={apiData.json_files}
                title="Selected Files for Genome Browser"
                showSearch={true}
                showFileDetails={true}
                onClearAll={() => {
                  selectedFiles.forEach((fileId) => toggleFile(fileId));
                }}
                maxDisplayFiles={8}
              />
            </Col>
          </Row>

          <Row>
            <Col lg={12}>

              <GenomeBrowserTranscriptListener />

              <div
                className="genome-browser-container"
                style={{
                  minHeight: "600px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "20px",
                  backgroundColor: "#ffffff",
                  width: "100%",
                }}
              >
                <div
                  ref={containerRef}
                  style={{
                    minHeight: "600px",
                    width: "100%",
                  }}
                >
                  {!plotScript && !bokehLoading && (
                    <div className="text-center py-5">
                      <div className="text-muted">
                        <i
                          className="ri-bar-chart-line"
                          style={{ fontSize: "3rem", opacity: 0.5 }}
                        />
                        <p className="mt-3 mb-0">
                          Genome browser will load here
                        </p>
                        <small>Loading plot from server...</small>
                      </div>
                    </div>
                  )}

                  {bokehLoading && (
                    <div className="text-center py-3">
                      <FadeLoader color="#1A6D52" size={30} />
                      <p className="mt-2 text-muted">Loading Bokeh plot...</p>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>

          <ToastContainer />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SingleGenomeBrowser;
